import { ActionButton, Text } from '@react-spectrum/s2';
import Redo from '@react-spectrum/s2/icons/Redo';
import Undo from '@react-spectrum/s2/icons/Undo';
import { style } from '@react-spectrum/s2/style' with { type: 'macro' };

import { useBridgeApi } from '@/api/BridgeApi.ts';
import { BlobImage } from '@/components/blob/BlobImage';
import { useOpenChatPanel } from '@/hooks/useOpenChatPanel.ts';
import { trackEvent } from '@/utils/fullstory';

import type { Artifact } from '../../../../../bridge/types/artifact.ts';

import { MessageRow } from './MessageRow.tsx';

interface EditedImageArtifactProps {
	artifactId: string;
	actionImageUrl: string;
	imageIndex?: number;
	originalImageUrl?: string;
	wasCropped?: boolean;
	via?: 'manual' | 'vibe_edit';
}

function getArtifactImageUrls(
	panelArtifact: Artifact | null | undefined,
	artifactId: string,
): string[] | null {
	const read = (content: Record<string, unknown>): string[] | null => {
		const urls = content.image_urls;
		return Array.isArray(urls) ? (urls as string[]) : null;
	};

	if (!panelArtifact) return null;

	if (panelArtifact.id === artifactId) {
		return read(panelArtifact.content as Record<string, unknown>);
	}

	// `children` is not on the shared Artifact type — a panel artifact standing in for a
	// process carries them at runtime, which is why the `in` guard is here at all. Naming
	// the shape keeps the guarded branch typed instead of collapsing to `{}`.
	type WithChildren = { children?: Array<{ id: string; content?: unknown }> };
	const children =
		('children' in panelArtifact
			? (panelArtifact as WithChildren).children
			: undefined) ?? [];
	const child = children.find((candidate) => candidate.id === artifactId);
	if (!child) return null;
	return read(child.content as Record<string, unknown>);
}

function buildLabel(
	via: 'manual' | 'vibe_edit' | undefined,
	imageIndex: number | undefined,
	wasCropped: boolean | undefined,
): string {
	const slidePrefix =
		imageIndex !== undefined ? `Slide ${imageIndex + 1} ` : '';
	if (via === 'vibe_edit') {
		return imageIndex !== undefined
			? `Slide ${imageIndex + 1} restyled`
			: 'Image restyled';
	}
	const action = wasCropped ? 'uploaded and rescaled' : 'updated';
	if (imageIndex !== undefined) {
		return `${slidePrefix}${action}`;
	}
	return wasCropped ? 'Image uploaded and rescaled' : 'Image updated';
}

export const EditedImageArtifact = ({
	artifactId,
	actionImageUrl,
	imageIndex,
	originalImageUrl,
	wasCropped,
	via,
}: EditedImageArtifactProps) => {
	const bridgeApi = useBridgeApi();
	const openChatPanel = useOpenChatPanel();
	const panelArtifact =
		bridgeApi.user_state?.chat_panel?.panel_type === 'artifact'
			? bridgeApi.user_state.chat_panel.artifact
			: null;

	const imageUrls = getArtifactImageUrls(panelArtifact, artifactId);
	const idx = imageIndex ?? 0;
	const currentImageUrl = imageUrls ? (imageUrls[idx] ?? null) : null;
	const isCurrentImageApplied = currentImageUrl === actionImageUrl;
	const isArtifactPresentInPanel = imageUrls != null;
	const showUndo =
		isArtifactPresentInPanel && isCurrentImageApplied && !!originalImageUrl;
	// Apply needs a url sitting at the index to replace. With the slide gone
	// (removed, so the index is past the end of the array) the write is refused
	// downstream for being out of range, so offering the button would be a lie.
	const showRedo =
		isArtifactPresentInPanel &&
		!!currentImageUrl &&
		!isCurrentImageApplied &&
		!!actionImageUrl;

	const handleUndo = () => {
		if (!originalImageUrl) return;
		trackEvent('Vibe Edit Discarded', {
			artifact_id: artifactId,
			image_index: idx,
			...(via ? { via } : {}),
		});
		bridgeApi.sendAction({
			type: 'do_revert_edited_image',
			artifact_id: artifactId,
			image_index: idx,
			image_url: originalImageUrl,
		});
	};

	const handleRedo = () => {
		trackEvent('Vibe Edit Accepted', {
			artifact_id: artifactId,
			image_index: idx,
			...(via ? { via } : {}),
		});
		bridgeApi.sendAction({
			type: 'do_apply_edited_image',
			artifact_id: artifactId,
			image_index: idx,
			image_url: actionImageUrl,
			// Name the url this card read at the index, which is the same value
			// the button's visibility was decided from. The slot can hold a later
			// edit rather than the pre-edit original — sequential edits on one
			// slide leave every earlier card applyable — so sending the original
			// would have the bridge refuse writes the button openly offers. The
			// bridge refuses only when the slide changed between this render and
			// the click, i.e. when the version being replaced is not the one the
			// user was looking at.
			...(currentImageUrl && { expected_current_url: currentImageUrl }),
		});
	};

	const handleOpenPanel = () => {
		trackEvent('Artifact Clicked', {
			panel_type: 'artifact',
			artifact_id: artifactId,
			image_index: idx,
			...(via ? { via } : {}),
		});
		openChatPanel({
			panel_type: 'artifact',
			item_id: artifactId,
		});
	};

	const label = buildLabel(via, imageIndex, wasCropped);

	return (
		<MessageRow>
			<div
				className="glass-card-sm glass-card-hover flex cursor-pointer gap-4 px-4
					py-3"
				role="button"
				tabIndex={0}
				aria-label={`Open ${label} in panel`}
				onClick={handleOpenPanel}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						// Space would otherwise scroll the page while the card has focus.
						e.preventDefault();
						handleOpenPanel();
					}
				}}
			>
				<div className="flex items-center gap-2">
					{/*
						Undo/redo are real <button> elements whose activation (click
						and keydown) bubbles up the DOM. Stop it here so pressing a
						control doesn't also trigger the card's open-panel handler.
					*/}
					<span
						onClick={(e) => e.stopPropagation()}
						onKeyDown={(e) => e.stopPropagation()}
					>
						{showRedo && (
							<ActionButton
								isQuiet
								onPress={handleRedo}
								aria-label="Apply this version"
								size="XS"
							>
								<Redo />
							</ActionButton>
						)}
						{showUndo && (
							<ActionButton
								isQuiet
								onPress={handleUndo}
								aria-label="Undo image update"
								size="XS"
							>
								<Undo />
							</ActionButton>
						)}
					</span>
					<Text
						styles={style({
							font: 'body',
							color: 'body',
							fontSize: 'body',
						})}
					>
						{label}
					</Text>
				</div>
				{actionImageUrl && (
					<BlobImage
						src={actionImageUrl}
						alt="Edited image"
						className="h-10 w-10 rounded-lg object-cover"
					/>
				)}
			</div>
		</MessageRow>
	);
};
